const toKey = (value) => String(value);

/** Display name from API (string or occasional array). */
export const formatHierarchyNodeName = (name) => {
  if (Array.isArray(name)) {
    const parts = name.filter((part) => part != null && String(part).trim() !== "");
    return parts.length > 0 ? parts.join(", ") : "Unnamed";
  }
  if (name == null || String(name).trim() === "") return "Unnamed";
  return String(name);
};

/** Nested nodes — API may use `items` or `children`. */
export const getHierarchyNodeChildren = (node) => {
  if (!node) return [];
  const items = Array.isArray(node.items) ? node.items : null;
  const children = Array.isArray(node.children) ? node.children : null;
  if (items?.length) return items;
  if (children?.length) return children;
  return items ?? children ?? [];
};

const hierarchyChildArrayKey = (node) => {
  const items = Array.isArray(node?.items) ? node.items : null;
  const children = Array.isArray(node?.children) ? node.children : null;
  if (items?.length) return "items";
  if (children?.length) return "children";
  if (items) return "items";
  if (children) return "children";
  return "items";
};

/** Stable node key for UI selection, drag, and URL (not shown in details). */
export const getHierarchyNodeKey = (node) => {
  if (!node) return null;
  if (node.hash_id != null && String(node.hash_id).trim() !== "") {
    return String(node.hash_id);
  }
  if (node.id != null) return String(node.id);
  if (node._id != null) return String(node._id);
  return null;
};

/** Leaf / linked product node (items+product_id or children+SERVICE/GOODS). */
export const isHierarchyProductNode = (node) => {
  if (!node) return false;
  if (node.product_id) return true;
  const type = String(node.type || "").toUpperCase();
  return type === "SERVICE" || type === "GOODS";
};

export const cloneHierarchyNodes = (nodes = []) =>
  nodes.map((node) => {
    const key = hierarchyChildArrayKey(node);
    return {
      ...node,
      [key]: cloneHierarchyNodes(getHierarchyNodeChildren(node)),
    };
  });

export const findHierarchyNodeMeta = (nodes, nodeKey, parentKey = null) => {
  const targetId = toKey(nodeKey);
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (toKey(getHierarchyNodeKey(node)) === targetId) {
      return {
        node,
        index,
        parentKey,
        siblings: nodes,
      };
    }
    const childMeta = findHierarchyNodeMeta(
      getHierarchyNodeChildren(node),
      nodeKey,
      getHierarchyNodeKey(node)
    );
    if (childMeta) return childMeta;
  }
  return null;
};

export const hierarchyContainsNode = (node, searchKey) => {
  const children = getHierarchyNodeChildren(node);
  if (children.length === 0) return false;
  const targetId = toKey(searchKey);
  return children.some(
    (child) =>
      toKey(getHierarchyNodeKey(child)) === targetId ||
      hierarchyContainsNode(child, searchKey)
  );
};

export const moveHierarchyNodeInTree = ({ tree, activeNodeKey, dropData, dropTypes }) => {
  const sourceMeta = findHierarchyNodeMeta(tree, activeNodeKey);
  if (!sourceMeta) return { moved: false };

  const sourceNodeId = toKey(activeNodeKey);
  let destinationParentKey = null;
  let destinationIndex = 0;

  if (dropData.dropType === dropTypes.ROOT_END) {
    destinationParentKey = null;
    destinationIndex = tree.length;
  } else {
    const targetMeta = findHierarchyNodeMeta(tree, dropData.targetId);
    if (!targetMeta) return { moved: false };

    const targetNodeId = toKey(dropData.targetId);

    if (
      dropData.dropType === dropTypes.INTO &&
      sourceNodeId === targetNodeId
    ) {
      return { moved: false };
    }

    if (dropData.dropType === dropTypes.INTO) {
      destinationParentKey = getHierarchyNodeKey(targetMeta.node);
      destinationIndex = getHierarchyNodeChildren(targetMeta.node).length;
    } else {
      return { moved: false };
    }
  }

  const activeNode = sourceMeta.node;
  if (
    destinationParentKey !== null &&
    hierarchyContainsNode(activeNode, destinationParentKey)
  ) {
    return { moved: false, invalidCycle: true };
  }

  const sameParent =
    toKey(sourceMeta.parentKey) === toKey(destinationParentKey);

  const [removedNode] = sourceMeta.siblings.splice(sourceMeta.index, 1);

  if (sameParent && sourceMeta.index < destinationIndex) {
    destinationIndex -= 1;
  }

  let destinationSiblings = tree;
  if (destinationParentKey !== null) {
    const destinationParentMeta = findHierarchyNodeMeta(tree, destinationParentKey);
    if (!destinationParentMeta?.node) return { moved: false };
    const parentKey = hierarchyChildArrayKey(destinationParentMeta.node);
    if (!Array.isArray(destinationParentMeta.node[parentKey])) {
      destinationParentMeta.node[parentKey] = [];
    }
    destinationSiblings = destinationParentMeta.node[parentKey];
  }

  const boundedDestinationIndex = Math.max(
    0,
    Math.min(destinationIndex, destinationSiblings.length)
  );
  destinationSiblings.splice(boundedDestinationIndex, 0, removedNode);

  return {
    moved: true,
    move: {
      hash_id: removedNode.hash_id ?? getHierarchyNodeKey(removedNode),
      parent_hash_id: destinationParentKey,
      position: boundedDestinationIndex,
      previous_parent_hash_id: sourceMeta.parentKey,
      previous_position: sourceMeta.index,
    },
  };
};

/** Lowercase text used for client-side hierarchy search. */
export const getHierarchyNodeSearchText = (node) => {
  const parts = [
    formatHierarchyNodeName(node?.name),
    node?.description || "",
    node?.code || "",
    node?.alias_name || "",
    node?.type || "",
    ...(Array.isArray(node?.tags) ? node.tags : []),
  ];
  return parts.join(" ").toLowerCase();
};

/** User-visible fields for the expanded details panel (no IDs). */
export const getHierarchyNodeDetailFields = (node) => {
  if (!node) return [];
  const fields = [];

  if (node.description) {
    fields.push({ label: "Description", value: node.description });
  }
  if (node.code) {
    fields.push({ label: "Code", value: node.code });
  }
  if (node.alias_name) {
    fields.push({ label: "Alias", value: node.alias_name });
  }
  if (node.type) {
    fields.push({ label: "Type", value: node.type });
  }
  if (Array.isArray(node.tags) && node.tags.length > 0) {
    fields.push({ label: "Tags", value: node.tags, isTags: true });
  }

  return fields;
};

export const countHierarchyNestedItems = (item) => {
  const childItems = getHierarchyNodeChildren(item);
  if (childItems.length === 0) return 0;
  return childItems.reduce(
    (acc, child) => acc + 1 + countHierarchyNestedItems(child),
    0
  );
};
